import { type RefObject, useCallback, useLayoutEffect, useRef } from 'react';
import type SwiperCore from 'swiper';
import { A11y, Mousewheel, Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Box, Flex } from '@chakra-ui/react';

import {
  type ExperienceAtfCardConfig,
  EXPERIENCE_ATF_SLIDE_WIDTH_PX,
  EXPERIENCE_ATF_SLIDE_WIDTH_PX_MOBILE,
} from '../constants';

import { ExperienceAtfHeroCard } from './experience-atf-hero-card';

import 'swiper/css';
import 'swiper/css/mousewheel';
import 'swiper/css/navigation';

export type ExperienceAtfHeroCarouselTrackProps = {
  bodyContentMaxWidth: string;
  cards: ExperienceAtfCardConfig[];
  isClientReady: boolean;
  isMobile: boolean;
  nextRef: RefObject<HTMLButtonElement | null>;
  onBeforeInit: (swiper: SwiperCore) => void;
  onCardClick: (card: ExperienceAtfCardConfig) => void;
  onDestroy: () => void;
  onResize: (swiper: SwiperCore) => void;
  onSlideChange: (swiper: SwiperCore) => void;
  onSwiper: (swiper: SwiperCore) => void;
  onVideo: (card: ExperienceAtfCardConfig) => void;
  prevRef: RefObject<HTMLButtonElement | null>;
  slidesStartInsetPx: number;
  viewedCardIdsRef: RefObject<Set<string>>;
};

/**
 * Swiper mutates the DOM (extra wrapper divs). SSR + first client paint often disagree → hydration
 * errors. Render a static scroll row until after mount, then swap to Swiper.
 */
export function ExperienceAtfHeroCarouselTrack({
  bodyContentMaxWidth,
  cards,
  isClientReady,
  isMobile,
  nextRef,
  onBeforeInit,
  onCardClick,
  onDestroy,
  onResize,
  onSlideChange,
  onSwiper,
  onVideo,
  prevRef,
  slidesStartInsetPx,
  viewedCardIdsRef,
}: ExperienceAtfHeroCarouselTrackProps) {
  const slideWidthPx = isMobile ? EXPERIENCE_ATF_SLIDE_WIDTH_PX_MOBILE : EXPERIENCE_ATF_SLIDE_WIDTH_PX;
  const slidesOffsetBeforePx = isMobile ? 16 : slidesStartInsetPx;
  const swiperRef = useRef<SwiperCore | null>(null);

  const handleSwiper = useCallback(
    (swiper: SwiperCore) => {
      swiperRef.current = swiper;
      onSwiper(swiper);
    },
    [onSwiper]
  );

  useLayoutEffect(() => {
    const swiper = swiperRef.current;
    if (!swiper || !isClientReady) return;
    if (swiper.params.slidesOffsetBefore !== slidesOffsetBeforePx) {
      swiper.params.slidesOffsetBefore = slidesOffsetBeforePx;
      swiper.update();
    }
  }, [isClientReady, slidesOffsetBeforePx]);

  const handleDestroy = useCallback(() => {
    swiperRef.current = null;
    onDestroy();
  }, [onDestroy]);

  const staticRowPaddingInlineStart = isMobile
    ? '16px'
    : ({
        base: '0px',
        md: '24px',
        lg: '48px',
        xl: `max(0px, calc((100vw - ${bodyContentMaxWidth}) / 2))`,
      } as const);

  if (!isClientReady) {
    return (
      <Flex
        gap={2}
        overflowX='auto'
        overflowY='hidden'
        paddingInlineEnd={isMobile ? '16px' : '0px'}
        paddingInlineStart={staticRowPaddingInlineStart}
        sx={{ WebkitOverflowScrolling: 'touch' }}
        width='100%'
      >
        {cards.map((card, index) => (
          <Box
            key={card.id}
            flexShrink={0}
            width={{
              base: `${EXPERIENCE_ATF_SLIDE_WIDTH_PX_MOBILE}px`,
              xl: `${EXPERIENCE_ATF_SLIDE_WIDTH_PX}px`,
            }}
          >
            <ExperienceAtfHeroCard
              card={card}
              dedupeViewIdsRef={viewedCardIdsRef}
              position={index + 1}
              onClick={onCardClick}
              onVideo={onVideo}
            />
          </Box>
        ))}
      </Flex>
    );
  }

  return (
    <Swiper
      key={`atf-${isMobile ? 'm' : 'd'}`}
      direction='horizontal'
      modules={[A11y, Mousewheel, Navigation]}
      slidesOffsetAfter={isMobile ? 16 : 0}
      slidesOffsetBefore={slidesOffsetBeforePx}
      slidesPerView='auto'
      spaceBetween={8}
      mousewheel={{
        forceToAxis: true,
        releaseOnEdges: true,
        thresholdDelta: 10,
      }}
      navigation={
        cards.length > 1
          ? {
              nextEl: nextRef.current,
              prevEl: prevRef.current,
            }
          : undefined
      }
      onBeforeInit={onBeforeInit}
      onDestroy={handleDestroy}
      onResize={onResize}
      onSlideChange={onSlideChange}
      onSwiper={handleSwiper}
    >
      {cards.map((card, index) => (
        <SwiperSlide
          key={card.id}
          style={{
            width: `${slideWidthPx}px`,
          }}
        >
          <ExperienceAtfHeroCard
            card={card}
            dedupeViewIdsRef={viewedCardIdsRef}
            position={index + 1}
            onClick={onCardClick}
            onVideo={onVideo}
          />
        </SwiperSlide>
      ))}
    </Swiper>
  );
}
